/**
 * Shared UI primitives for the TubeForge v2 design system.
 * These replace the old Tailwind-based components on redesigned pages.
 * Non-redesigned pages (admin, billing, settings, auth) keep using
 * the original components/ui/* library.
 */
import { type CSSProperties, type ReactNode, type MouseEventHandler } from "react";

// ── Status config ─────────────────────────────────────────────────
export const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; dot: string }
> = {
  draft:                 { label: "Draft",          color: "var(--tf-fg-muted)", dot: "var(--tf-rule-2)" },
  script_generating:     { label: "Generating…",    color: "var(--tf-info)",     dot: "var(--tf-info)" },
  // (next drop §4g, golden run 0b011d6f: a video waiting at the customer's own gate read "Storyboard" in the list while
  //  the run said `running` — the five-situation word is NEEDS YOU, on every surface, the same as the video's page)
  script_ready:          { label: "Needs you · script",     color: "var(--tf-accent)",   dot: "var(--tf-accent)" },
  script_approved:       { label: "Script done",    color: "var(--tf-info)",     dot: "var(--tf-info)" },
  storyboard_generating: { label: "Storyboard…",    color: "var(--tf-accent)",   dot: "var(--tf-accent)" },
  storyboard_ready:      { label: "Needs you · storyboard", color: "var(--tf-accent)",   dot: "var(--tf-accent)" },
  storyboard_approved:   { label: "Approved",       color: "var(--tf-accent)",   dot: "var(--tf-accent)" },
  generating_clips:      { label: "Generating…",    color: "var(--tf-warning)",  dot: "var(--tf-warning)" },
  voiceover_generating:  { label: "Voiceover…",     color: "var(--tf-warning)",  dot: "var(--tf-warning)" },
  rendering:             { label: "Rendering",      color: "var(--tf-warning)",  dot: "var(--tf-warning)" },
  complete:              { label: "Ready",           color: "var(--tf-success)",  dot: "var(--tf-success)" },
  publishing:            { label: "Publishing…",    color: "var(--tf-success)",  dot: "var(--tf-success)" },
  published:             { label: "Published",      color: "var(--tf-success)",  dot: "var(--tf-success)" },
  publish_failed:        { label: "Publish failed", color: "var(--tf-danger)",   dot: "var(--tf-danger)" },
  failed:                { label: "Failed",         color: "var(--tf-danger)",   dot: "var(--tf-danger)" },
  // session 7: neither is a failure — a cancel the customer made, and a draft that waited too long
  cancelled:             { label: "Cancelled",      color: "var(--tf-fg-muted)", dot: "var(--tf-rule-2)" },
  expired:               { label: "Expired",        color: "var(--tf-fg-muted)", dot: "var(--tf-rule-2)" },   // the draft card says: start again with the same choices
};

// ── StatusPill ─────────────────────────────────────────────────────
export function StatusPill({ status, size = "md", label }: { status: string; size?: "sm" | "md";
    /** Session 6 W2: a more precise word for the same status (a rendered video's finishing state). */
    label?: string }) {
  const s = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const isSm = size === "sm";
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: isSm ? "2px 8px" : "3px 10px",
        fontSize: isSm ? 11 : 12,
        fontWeight: 500,
        borderRadius: 999,
        background: "var(--tf-surface)",
        color: s.color,
        border: "1px solid var(--tf-rule)",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }} />
      {label ?? s.label}
    </span>
  );
}

// ── Icon ───────────────────────────────────────────────────────────
export function Icon({ name, size = 16, color = "currentColor" }: { name: string; size?: number; color?: string }) {
  const sw = 1.6;
  const props = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: sw, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  const paths: Record<string, ReactNode> = {
    home:        <><path d="M3 12l9-9 9 9"/><path d="M5 10v10h14V10"/></>,
    video:       <><rect x="3" y="6" width="14" height="12" rx="2"/><path d="M17 10l4-2v8l-4-2z"/></>,
    plus:        <><path d="M12 5v14M5 12h14"/></>,
    layers:      <><path d="M12 3l9 5-9 5-9-5 9-5z"/><path d="M3 13l9 5 9-5"/></>,
    stats:       <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/></>,
    settings:    <><circle cx="12" cy="12" r="3"/><path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.5-2.4 1a7 7 0 0 0-2-1.2L14 3h-4l-.4 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.5 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.5 2.4-1a7 7 0 0 0 2 1.2L10 21h4l.4-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.5-2-1.5c.1-.4.1-.8.1-1.2z"/></>,
    search:      <><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></>,
    bell:        <><path d="M6 9a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8"/><path d="M10 22a2 2 0 0 0 4 0"/></>,
    chevronDown: <><path d="m6 9 6 6 6-6"/></>,
    chevronRight:<><path d="m9 6 6 6-6 6"/></>,
    chevronLeft: <><path d="m15 6-6 6 6 6"/></>,
    arrowRight:  <><path d="M5 12h14M13 5l7 7-7 7"/></>,
    arrowLeft:   <><path d="M19 12H5M11 19l-7-7 7-7"/></>,
    play:        <><polygon points="6 4 20 12 6 20" fill={color} stroke="none"/></>,
    download:    <><path d="M12 4v12M6 10l6 6 6-6M4 20h16"/></>,
    upload:      <><path d="M12 20V8M6 14l6-6 6 6M4 4h16"/></>,
    refresh:     <><path d="M21 12a9 9 0 1 1-3-6.7L21 8"/><path d="M21 3v5h-5"/></>,
    check:       <><path d="m5 13 4 4 10-12"/></>,
    x:           <><path d="m6 6 12 12M18 6 6 18"/></>,
    edit:        <><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4z"/></>,
    trash:       <><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></>,
    dots:        <><circle cx="5" cy="12" r="1" fill={color}/><circle cx="12" cy="12" r="1" fill={color}/><circle cx="19" cy="12" r="1" fill={color}/></>,
    filter:      <><path d="M3 5h18M6 12h12M10 19h4"/></>,
    grid:        <><rect x="4" y="4" width="6" height="6"/><rect x="14" y="4" width="6" height="6"/><rect x="4" y="14" width="6" height="6"/><rect x="14" y="14" width="6" height="6"/></>,
    list:        <><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></>,
    menu:        <><path d="M3 6h18M3 12h18M3 18h18"/></>,
    youtube:     <><rect x="2" y="6" width="20" height="12" rx="3"/><polygon points="10 9 16 12 10 15" fill={color} stroke="none"/></>,
    sparkle:     <><path d="M12 3v6M12 15v6M3 12h6M15 12h6"/></>,
    help:        <><circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 4"/><circle cx="12" cy="17" r="0.5" fill={color}/></>,
    folder:      <><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></>,
    image:       <><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></>,
    eye:         <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    clock:       <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
    creditCard:  <><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></>,
    logOut:      <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
    shield:      <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
  };
  return <svg {...props}>{paths[name] || null}</svg>;
}

// ── Button ─────────────────────────────────────────────────────────
type BtnVariant = "primary" | "ghost" | "subtle" | "danger";
type BtnSize = "sm" | "md" | "lg";

export function Btn({
  children, variant = "primary", size = "md", icon, onClick, disabled, style, fullWidth, loading, type = "button", ariaLabel,
}: {
  children?: ReactNode; variant?: BtnVariant; size?: BtnSize;
  icon?: string; onClick?: MouseEventHandler; disabled?: boolean;
  style?: CSSProperties; fullWidth?: boolean; loading?: boolean;
  type?: "button" | "submit";
  /** (B102) the button's name when its content is an icon only */ ariaLabel?: string;
}) {
  const sizes: Record<BtnSize, CSSProperties> = {
    sm: { padding: "6px 10px", fontSize: 13 },
    md: { padding: "8px 14px", fontSize: 14 },
    lg: { padding: "11px 18px", fontSize: 14 },
  };
  const variants: Record<BtnVariant, CSSProperties> = {
    primary: { background: "var(--tf-accent)", color: "#fff", border: "1px solid var(--tf-accent)" },
    ghost:   { background: "transparent",      color: "var(--tf-fg)", border: "1px solid var(--tf-rule-2)" },
    subtle:  { background: "var(--tf-surface)", color: "var(--tf-fg)", border: "1px solid var(--tf-rule)" },
    danger:  { background: "transparent",      color: "var(--tf-danger)", border: "1px solid var(--tf-rule-2)" },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      aria-label={ariaLabel}
      disabled={disabled || loading}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        borderRadius: 8,
        fontFamily: "inherit", fontWeight: 500,
        cursor: (disabled || loading) ? "not-allowed" : "pointer",
        opacity: (disabled || loading) ? 0.5 : 1,
        width: fullWidth ? "100%" : "auto",
        justifyContent: fullWidth ? "center" : "flex-start",
        transition: "filter .15s ease, background .15s ease",
        ...sizes[size],
        ...variants[variant],
        ...style,
      }}
    >
      {loading && <TfSpinner size={14} />}
      {icon && !loading && <Icon name={icon} size={14} />}
      {children}
    </button>
  );
}

// ── Card ───────────────────────────────────────────────────────────
export function TfCard({
  children, padding = 16, style, onClick,
}: {
  children: ReactNode; padding?: number; style?: CSSProperties;
  onClick?: MouseEventHandler;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--tf-bg)",
        border: "1px solid var(--tf-rule)",
        borderRadius: 10,
        boxShadow: "var(--tf-shadow-sm)",
        padding,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── StatCard ──────────────────────────────────────────────────────
export function StatCard({ value, label, sub, trend }: {
  value: string | number; label: string; sub?: string; trend?: string;
}) {
  return (
    <TfCard padding={16}>
      <div className="tf-label" style={{ marginBottom: 6, fontSize: 10 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: "-0.02em" }}>{value}</div>
        {trend && (
          <span style={{ fontSize: 12, color: trend.startsWith("-") ? "var(--tf-danger)" : "var(--tf-success)" }}>
            {trend.startsWith("-") ? "↓" : "↑"} {trend.replace(/^[-+]/, "")}
          </span>
        )}
      </div>
      {sub && <div className="tf-muted" style={{ fontSize: 12, marginTop: 4 }}>{sub}</div>}
    </TfCard>
  );
}

// ── SectionHeader ─────────────────────────────────────────────────
export function SectionHeader({ title, subtitle, action }: {
  title: string; subtitle?: string; action?: ReactNode;
}) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 16, gap: 16 }}>
      <div>
        <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>{title}</h2>
        {subtitle && <p className="tf-muted" style={{ fontSize: 13, marginTop: 4 }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────
export function EmptyState({ icon, title, sub, action }: {
  icon?: string; title: string; sub?: string; action?: ReactNode;
}) {
  return (
    <div style={{ textAlign: "center", padding: "48px 24px", color: "var(--tf-fg-muted)" }}>
      {icon && (
        <div style={{ marginBottom: 14, display: "inline-flex", padding: 14, background: "var(--tf-surface)", borderRadius: "50%", color: "var(--tf-fg-faint)" }}>
          <Icon name={icon} size={24} />
        </div>
      )}
      <h3 style={{ fontSize: 16, color: "var(--tf-fg)", margin: 0 }}>{title}</h3>
      {sub && <p style={{ marginTop: 6, fontSize: 13, maxWidth: 360, marginInline: "auto" }}>{sub}</p>}
      {action && <div style={{ marginTop: 18 }}>{action}</div>}
    </div>
  );
}

// ── ToggleGroup ───────────────────────────────────────────────────
export function ToggleGroup({ value, onChange, options }: {
  value: string; onChange: (v: string) => void;
  options: Array<{ value: string; label: string; icon?: string }>;
}) {
  return (
    <div style={{ display: "inline-flex", padding: 2, background: "var(--tf-surface)", borderRadius: 8, border: "1px solid var(--tf-rule)" }}>
      {options.map(o => {
        const sel = o.value === value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              padding: "6px 12px",
              background: sel ? "var(--tf-bg)" : "transparent",
              border: 0, borderRadius: 6,
              fontFamily: "inherit", fontSize: 13,
              color: sel ? "var(--tf-fg)" : "var(--tf-fg-muted)",
              cursor: "pointer",
              boxShadow: sel ? "var(--tf-shadow-sm)" : "none",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            {o.icon && <Icon name={o.icon} size={14} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Checkbox ──────────────────────────────────────────────────────
export function Checkbox({ checked, onChange, label }: { checked: boolean; onChange: () => void; label?: string }) {
  return (
    <button
      role="checkbox" aria-checked={checked} aria-label={label ?? "Select"}   // (B102) a checkbox, named
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      style={{
        width: 18, height: 18, padding: 0, borderRadius: 4,
        border: "1.5px solid " + (checked ? "var(--tf-accent)" : "var(--tf-rule-2)"),
        background: checked ? "var(--tf-accent)" : "var(--tf-bg)",
        cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {checked && (
        <svg width="11" height="11" viewBox="0 0 11 11">
          <path d="M2 5.5l2.5 2.5L9 3" stroke="white" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </button>
  );
}

// ── Spinner ───────────────────────────────────────────────────────
export function TfSpinner({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ animation: "tf-spin 0.9s linear infinite" }}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" fill="none" opacity="0.2" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    </svg>
  );
}

// ── FormatBadge ───────────────────────────────────────────────────
export function FormatBadge({ format }: { format: string }) {
  const label = format === "shorts" ? "9:16" : "16:9";
  return (
    <span className="tf-mono" style={{
      fontSize: 10, letterSpacing: "0.05em",
      padding: "2px 6px",
      border: "1px solid var(--tf-rule)",
      borderRadius: 4,
      color: "var(--tf-fg-muted)",
      background: "var(--tf-surface)",
      textTransform: "uppercase",
    }}>{label}</span>
  );
}

// ── VideoThumb ────────────────────────────────────────────────────
export function VideoThumb({ title, format, duration, thumbnailUrl, height = 110, showTitle = true }: {
  title: string; format: string; duration?: string | null;
  thumbnailUrl?: string | null; height?: number; showTitle?: boolean;
}) {
  const aspect = format === "shorts" ? "9 / 16" : "16 / 9";
  const bg = thumbnailUrl || "#444";
  return (
    <div style={{
      position: "relative", aspectRatio: aspect, height,
      background: bg.startsWith("http") ? undefined : bg,
      backgroundImage: bg.startsWith("http") ? `url(${bg})` : undefined,
      backgroundSize: "cover", backgroundPosition: "center",
      borderRadius: 6, overflow: "hidden", flexShrink: 0,
    }}>
      {showTitle && (
        <div style={{
          position: "absolute", inset: 0,
          padding: "8px 10px",
          color: "#fff", fontSize: 12, lineHeight: 1.25, fontWeight: 500,
          background: "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)",
          display: "flex", flexDirection: "column", justifyContent: "flex-end",
        }}>
          <div style={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const }}>{title}</div>
        </div>
      )}
      {duration && (
        <div style={{
          position: "absolute", bottom: 6, right: 6,
          background: "rgba(0,0,0,0.85)", color: "#fff",
          padding: "1px 5px", fontSize: 10,
          fontFamily: "var(--tf-mono)", borderRadius: 3,
        }}>{duration}</div>
      )}
    </div>
  );
}

// ── PageContainer ─────────────────────────────────────────────────
export function PageContainer({ title, subtitle, action, children, maxWidth = 1200, dense }: {
  title?: string; subtitle?: string; action?: ReactNode;
  children: ReactNode; maxWidth?: number; dense?: boolean;
}) {
  return (
    <div style={{ padding: dense ? "20px 24px 40px" : "32px 32px 60px", maxWidth, marginInline: "auto" }}>
      {title && (
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", margin: 0 }}>{title}</h1>
            {subtitle && <p className="tf-muted" style={{ fontSize: 14, marginTop: 6 }}>{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

// ── LineChart (SVG sparkline) ─────────────────────────────────────────
export function LineChart({ data, height = 120, color = "var(--tf-accent, #4f46e5)" }: {
  data: number[]; height?: number; color?: string;
}) {
  if (!data.length) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = Math.max(max - min, 1);
  const w = 600;
  const h = height;
  const points = data.map((v, i) => {
    const x = (i / Math.max(data.length - 1, 1)) * w;
    const y = h - ((v - min) / range) * (h - 16) - 8;
    return [x, y];
  });
  const linePath = points.map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
  const areaPath = linePath + ` L${w} ${h} L0 ${h} Z`;
  const gradId = "lc-" + Math.random().toString(36).slice(2, 8);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height: "100%", display: "block" }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.18} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── SpecRow (label → value) ───────────────────────────────────────────
export function SpecRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "8px 0",
      borderBottom: last ? "none" : "1px solid var(--tf-border)",
      fontSize: 13,
    }}>
      <span className="tf-muted">{label}</span>
      <span style={{ color: "var(--tf-fg)", fontWeight: 500 }}>{value}</span>
    </div>
  );
}

// ── ChannelChip ───────────────────────────────────────────────────────
export function ChannelChip({ name, color, size = "md" }: { name: string; color?: string; size?: "sm" | "md" }) {
  const s = size === "sm" ? 11 : 13;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: s,
    }}>
      {color && <span style={{ width: 7, height: 7, borderRadius: 2, background: color, flexShrink: 0 }} />}
      <span style={{ color: "var(--tf-fg-muted)" }}>{name}</span>
    </span>
  );
}
